import styled from "styled-components";
import { Bio } from "../../data/constants";
import { FaGithub, FaLinkedin, FaInstagram } from "react-icons/fa";
import { FiFileText } from "react-icons/fi";
import { RoseWindow } from "../../studio/SacredGeometry";

const Wrap = styled.footer`
  position: relative;
  background: linear-gradient(
    180deg,
    ${({ theme }) => theme.bg} 0%,
    ${({ theme }) => theme.bgDeep} 100%
  );
  border-top: 1px solid ${({ theme }) => theme.border};

  /* luminous gold hairline above the footer */
  &::before {
    content: "";
    position: absolute;
    top: -1px;
    left: 50%;
    transform: translateX(-50%);
    width: min(720px, 80%);
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      ${({ theme }) => theme.gold} 50%,
      transparent
    );
    box-shadow: 0 0 16px ${({ theme }) => theme.goldGlow};
    opacity: 0.85;
  }
`;

const Inner = styled.div`
  max-width: 1080px;
  margin: 0 auto;
  padding: 48px 24px 40px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 28px;

  @media ${({ theme }) => theme.bp.md} {
    flex-direction: column;
    text-align: center;
    gap: 30px;
  }
`;

const Brand = styled.div`
  .name {
    font-family: ${({ theme }) => theme.fonts.serif};
    font-weight: 600;
    font-size: 30px;
    letter-spacing: 0.01em;
    color: ${({ theme }) => theme.gold};
  }
  .sub {
    font-family: ${({ theme }) => theme.fonts.sans};
    font-size: 13.5px;
    color: ${({ theme }) => theme.textMuted};
    margin-top: 6px;
    letter-spacing: 0.02em;
  }
`;

const Links = styled.nav`
  display: flex;
  align-items: center;
  gap: 12px;

  a {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border-radius: 12px;
    border: 1px solid ${({ theme }) => theme.border};
    background: ${({ theme }) => theme.glass};
    color: ${({ theme }) => theme.textMuted};
    font-size: 18px;
    backdrop-filter: blur(6px);
    transition: color 0.25s ease, border-color 0.25s ease,
      box-shadow 0.25s ease, transform 0.25s ease;

    &:hover {
      color: ${({ theme }) => theme.gold};
      border-color: ${({ theme }) => theme.borderHi};
      box-shadow: 0 0 20px ${({ theme }) => theme.goldGlow};
      transform: translateY(-2px);
    }
  }
`;

/* center group: chakra above the benediction, compact for the same row */
const Sacred = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;

  .rose {
    color: ${({ theme }) => theme.gold};
    opacity: 0.4;
    line-height: 0;
  }

  .benediction {
    font-family: ${({ theme }) => theme.fonts.serif};
    font-style: italic;
    font-size: 16px;
    letter-spacing: 0.04em;
    white-space: nowrap;
    color: ${({ theme }) => theme.goldSoft};
  }
`;

const Bottom = styled.div`
  border-top: 1px solid ${({ theme }) => theme.border};
  text-align: center;
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 12px;
  letter-spacing: 0.06em;
  color: ${({ theme }) => theme.textFaint};
  padding: 22px 24px;
  background: ${({ theme }) => theme.veil};
`;

export default function Footer() {
  const year = new Date().getFullYear();

  return (
    <Wrap>
      <Inner>
        <Brand>
          <div className="name">{Bio.name}</div>
          <div className="sub">
            {Bio.title} @ {Bio.company} · {Bio.location}
          </div>
        </Brand>

        <Sacred>
          <div className="rose">
            <RoseWindow size={88} />
          </div>
          <p className="benediction">Crafted with intention.</p>
        </Sacred>

        <Links aria-label="Social links">
          <a href={Bio.github} target="_blank" rel="noreferrer" aria-label="GitHub">
            <FaGithub />
          </a>
          <a href={Bio.linkedin} target="_blank" rel="noreferrer" aria-label="LinkedIn">
            <FaLinkedin />
          </a>
          <a href={Bio.insta} target="_blank" rel="noreferrer" aria-label="Instagram">
            <FaInstagram />
          </a>
          <a href={Bio.resume} target="_blank" rel="noreferrer" aria-label="Résumé">
            <FiFileText />
          </a>
        </Links>
      </Inner>

      <Bottom>
        © {year} {Bio.name}
      </Bottom>
    </Wrap>
  );
}
